import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import { Calendar, Clock, User, Stethoscope, Plus, Search, Loader2, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function AppointmentsPage() {
  const { user } = useAuth();
  const api = useApi();
  const navigate = useNavigate();
  
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [patientTab, setPatientTab] = useState('existing');
  const [patientSearch, setPatientSearch] = useState('');
  const [doctorSearch, setDoctorSearch] = useState('');
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [filteredDoctors, setFilteredDoctors] = useState([]);
  
  // Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  
  const isSuperadmin = user?.role === 'superadmin';
  const canCreateAppointment = user?.role === 'clinic_admin' || user?.role === 'nurse';
  
  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: '',
    doctor_name: '',
    doctor_nmc_number: '',
    appointment_date: '',
    appointment_time: '',
    appointment_type: 'checkup',
    notes: '',
    send_email: true
  });

  const [newPatientData, setNewPatientData] = useState({
    full_name: '',
    date_of_birth: '',
    gender: '',
    phone: '',
    email: '',
    address: '',
    medical_history: ''
  });

  useEffect(() => {
    loadAppointments();
    loadStats();
    if (canCreateAppointment) {
      loadDoctors();
      loadPatients();
      loadTimeSlots();
    }
  }, []);

  // Reload time slots when appointment date changes
  useEffect(() => {
    if (formData.appointment_date && canCreateAppointment) {
      loadTimeSlots();
    }
  }, [formData.appointment_date]);


  useEffect(() => {
    if (patientSearch) {
      const filtered = patients.filter(p => 
        p.full_name?.toLowerCase().includes(patientSearch.toLowerCase()) ||
        p.phone?.includes(patientSearch) ||
        p.patient_id?.toLowerCase().includes(patientSearch.toLowerCase())
      );
      setFilteredPatients(filtered);
    } else {
      // Show all patients when search is empty (not selected yet)
      if (!formData.patient_id) {
        setFilteredPatients(patients);
      } else {
        setFilteredPatients([]);
      }
    }
  }, [patientSearch, patients, formData.patient_id]);

  useEffect(() => {
    // Start with all doctors or clinic-filtered doctors
    let baseList = doctors;
    if (user?.clinic_id && (user?.role === 'clinic_admin' || user?.role === 'nurse')) {
      baseList = doctors.filter(d => d.clinic_id === user.clinic_id);
    }
    
    // Apply search filter on top of clinic filter
    if (doctorSearch) {
      const filtered = baseList.filter(d => 
        d.full_name?.toLowerCase().includes(doctorSearch.toLowerCase()) ||
        d.specialist?.toLowerCase().includes(doctorSearch.toLowerCase()) ||
        d.nmc_id?.toLowerCase().includes(doctorSearch.toLowerCase())
      );
      setFilteredDoctors(filtered);
    } else {
      setFilteredDoctors(baseList);
    }
  }, [doctorSearch, doctors, user]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await api.get('/appointments');
      
      // Sort appointments by date and time (earliest first)
      const sorted = data.sort((a, b) => {
        // Compare dates first
        const dateA = new Date(a.appointment_date);
        const dateB = new Date(b.appointment_date);
        const dateCompare = dateA - dateB;
        
        if (dateCompare !== 0) return dateCompare;
        
        // If same date, compare times
        // Convert time strings to comparable format
        const timeA = convertToMinutes(a.appointment_time || '');
        const timeB = convertToMinutes(b.appointment_time || '');
        return timeA - timeB;
      });
      
      setAppointments(sorted);
    } catch (error) {
      console.error('Failed to load appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setLoading(false);
    }
  };

  // Convert time string (e.g., "10:30 AM" or "02:15 PM") to minutes since midnight
  const convertToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    
    try {
      // Handle 12-hour format (e.g., "10:30 AM")
      const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        let hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const period = match[3].toUpperCase();
        
        // Convert to 24-hour format
        if (period === 'PM' && hours !== 12) {
          hours += 12;
        } else if (period === 'AM' && hours === 12) {
          hours = 0;
        }
        
        return hours * 60 + minutes;
      }
      
      // Handle 24-hour format (e.g., "14:30")
      const match24 = timeStr.match(/(\d{1,2}):(\d{2})/);
      if (match24) {
        const hours = parseInt(match24[1], 10);
        const minutes = parseInt(match24[2], 10);
        return hours * 60 + minutes;
      }
    } catch (error) {
      console.error('Error parsing time:', timeStr, error);
    }
    
    return 0;
  };

  const loadStats = async () => {
    try {
      const data = await api.get('/appointments/stats');
      setStats(data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadDoctors = async () => {
    try {
      const data = await api.get('/doctors?active_only=true');
      setDoctors(data);
      setFilteredDoctors(data);
    } catch (error) {
      console.error('Failed to load doctors:', error);
      toast.error('Failed to load doctors');
    }
  };

  const loadPatients = async () => {
    try {
      const data = await api.get('/patients/');
      setPatients(data);
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    }
  };

  const loadTimeSlots = async () => {
    try {
      const date = formData.appointment_date || new Date().toISOString().split('T')[0];
      const data = await api.get(`/appointments/time-slots?date=${date}`);
      setTimeSlots(data.slots || []);
    } catch (error) {
      console.error('Failed to load time slots:', error);
    }
  };

  const handleCreateNewPatient = async () => {
    if (!newPatientData.full_name || !newPatientData.phone || !newPatientData.gender) {
      toast.error('Please fill required fields: Name, Phone, Gender');
      return;
    }

    try {
      setLoading(true);
      const patient = await api.post('/patients/', newPatientData);
      toast.success('Patient created successfully!');
      
      // Select the new patient and switch to appointment details
      setFormData({ ...formData, patient_id: patient.id });
      setPatientTab('existing');
      
      // Refresh patients list
      await loadPatients();
    } catch (error) {
      console.error('Failed to create patient:', error);
      toast.error(error.response?.data?.detail || 'Failed to create patient');
    } finally {
      setLoading(false);
    }
  };

  const handleDoctorSelect = (doctorId) => {
    const doctor = doctors.find(d => d.id === doctorId);
    if (doctor) {
      setFormData({
        ...formData,
        doctor_id: doctorId,
        doctor_name: doctor.full_name || `${doctor.first_name || ''} ${doctor.last_name || ''}`,
        doctor_nmc_number: doctor.nmc_number || doctor.internal_id || ''
      });
      setDoctorSearch('');
    }
  };

  const handlePatientSelect = (patientId) => {
    setFormData({ ...formData, patient_id: patientId });
    setPatientSearch('');
    setFilteredPatients([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.patient_id || !formData.doctor_id || !formData.appointment_date || !formData.appointment_time) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      setLoading(true);
      await api.post('/appointments', {
        ...formData,
        appointment_date: new Date(formData.appointment_date).toISOString()
      });
      
      toast.success('Appointment created! Email with QR code sent to patient.');
      setDialogOpen(false);
      resetForm();
      loadAppointments();
      loadStats();
    } catch (error) {
      console.error('Failed to create appointment:', error);
      toast.error(error.response?.data?.detail || 'Failed to create appointment');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      patient_id: '',
      doctor_id: '',
      doctor_name: '',
      doctor_nmc_number: '',
      appointment_date: '',
      appointment_time: '',
      appointment_type: 'checkup',
      notes: '',
      send_email: true
    });
    setNewPatientData({
      full_name: '',
      date_of_birth: '',
      gender: '',
      phone: '',
      email: '',
      address: '',
      medical_history: ''
    });
    setPatientSearch('');
    setDoctorSearch('');
    setPatientTab('existing');
  };

  const handleStatusChange = async () => {
    if (!selectedAppointment || !newStatus) return;
    
    // If rescheduled, validate date and time
    if (newStatus === 'rescheduled' && (!rescheduleDate || !rescheduleTime)) {
      toast.error('Please select new date and time for rescheduling');
      return;
    }
    
    setLoading(true);
    try {
      const updateData = { status: newStatus };
      
      // Add reschedule data if status is rescheduled
      if (newStatus === 'rescheduled') {
        updateData.appointment_date = rescheduleDate;
        updateData.appointment_time = rescheduleTime;
      }
      
      await api.put(`/appointments/${selectedAppointment.id}`, updateData);
      
      toast.success(
        newStatus === 'rescheduled' 
          ? `Appointment rescheduled to ${rescheduleDate} at ${rescheduleTime}` 
          : `Appointment status updated to ${newStatus}`
      );
      setStatusDialogOpen(false);
      setSelectedAppointment(null);
      setNewStatus('');
      setRescheduleDate('');
      setRescheduleTime('');
      loadAppointments();
      loadStats();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'Scheduled' },
      confirmed: { bg: 'bg-indigo-100 text-indigo-700', icon: CheckCircle, label: 'Confirmed' },
      in_progress: { bg: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'In Progress' },
      completed: { bg: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Completed' },
      cancelled: { bg: 'bg-red-100 text-red-700', icon: XCircle, label: 'Cancelled' },
      no_show: { bg: 'bg-gray-100 text-gray-700', icon: AlertCircle, label: 'No Show' },
      rescheduled: { bg: 'bg-purple-100 text-purple-700', icon: Calendar, label: 'Rescheduled' }
    };
    return styles[status] || styles.scheduled;
  };

  const selectedPatient = patients.find(p => p.id === formData.patient_id);
  const selectedDoctor = doctors.find(d => d.id === formData.doctor_id);

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-purple-900 mb-2">Appointments</h1>
            <p className="text-gray-600">Manage patient appointments</p>
          </div>
          {canCreateAppointment && (
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Appointment
            </Button>
          )}
        </div>

        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {stats.type === 'clinic_total' ? (
              <>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-2xl font-bold">{stats.total}</div>
                    <div className="text-sm text-gray-600">Total</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-2xl font-bold text-blue-600">{stats.scheduled}</div>
                    <div className="text-sm text-gray-600">Scheduled</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
                    <div className="text-sm text-gray-600">Completed</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <div className="text-2xl font-bold text-orange-600">{stats.upcoming_7_days}</div>
                    <div className="text-sm text-gray-600">Next 7 Days</div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="col-span-full">
                <CardHeader>
                  <CardTitle>Clinic-wise Appointments</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {stats.clinics?.map(clinic => (
                      <div key={clinic._id} className="flex justify-between p-3 bg-gray-50 rounded">
                        <span className="font-medium">{clinic.clinic_name}</span>
                        <div className="flex gap-4 text-sm">
                          <span>Total: {clinic.total}</span>
                          <span className="text-blue-600">Scheduled: {clinic.scheduled}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Appointments List */}
        <Card>
          <CardHeader>
            <CardTitle>All Appointments ({appointments.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && !appointments.length ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
              </div>
            ) : appointments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <p>No appointments scheduled yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map(apt => {
                  const statusStyle = getStatusBadge(apt.status);
                  const StatusIcon = statusStyle.icon;
                  
                  return (
                    <div key={apt.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <User className="h-5 w-5 text-gray-400" />
                            <div>
                              <div 
                                className="font-semibold text-lg cursor-pointer hover:text-purple-600 transition-colors"
                                onClick={() => navigate(`/patient/${apt.patient_id}`)}
                              >
                                {apt.patient_name}
                              </div>
                              <div className="text-sm text-gray-600">{apt.patient_phone}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-6 text-sm text-gray-600 ml-8">
                            <div className="flex items-center gap-2">
                              <Stethoscope className="h-4 w-4" />
                              <span>{apt.doctor_name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              <span>{format(new Date(apt.appointment_date), 'MMM dd, yyyy')}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              <span>{apt.appointment_time}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-col items-end gap-2">
                          <Badge 
                            className={`${statusStyle.bg} flex items-center gap-1 cursor-pointer hover:opacity-80`}
                            onClick={() => {
                              setSelectedAppointment(apt);
                              setNewStatus(apt.status);
                              // Pre-populate reschedule fields with current appointment values
                              if (apt.appointment_date) {
                                // Format date to YYYY-MM-DD for input
                                const dateObj = new Date(apt.appointment_date);
                                const formattedDate = dateObj.toISOString().split('T')[0];
                                setRescheduleDate(formattedDate);
                              }
                              if (apt.appointment_time) {
                                setRescheduleTime(apt.appointment_time);
                              }
                              setStatusDialogOpen(true);
                            }}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {statusStyle.label}
                          </Badge>
                          {apt.notes && (
                            <p className="text-xs text-gray-500 max-w-xs">Notes: {apt.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Appointment Dialog - Part 2 continues... */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New Appointment</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Patient Selection with Tabs */}
              <Tabs value={patientTab} onValueChange={setPatientTab}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="existing">Existing Patient</TabsTrigger>
                  <TabsTrigger value="new">New Patient</TabsTrigger>
                </TabsList>
                
                <TabsContent value="existing" className="space-y-4">
                  <div>
                    <Label>Search Patient *</Label>
                    <div className="relative">
                      <Input
                        placeholder="Search by name, phone, or patient ID..."
                        value={patientSearch}
                        onChange={(e) => setPatientSearch(e.target.value)}
                        disabled={!!formData.patient_id}
                      />
                      {!formData.patient_id && filteredPatients.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
                          {filteredPatients.map(patient => (
                            <div
                              key={patient.id}
                              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                              onClick={() => handlePatientSelect(patient.id)}
                            >
                              <div className="font-medium">{patient.full_name}</div>
                              <div className="text-sm text-gray-600">{patient.phone} • {patient.patient_id}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {!formData.patient_id && patients.length === 0 && (
                        <div className="mt-2 text-sm text-gray-500">
                          No patients found. Create a new patient using the "New Patient" tab.
                        </div>
                      )}
                    </div>
                    {selectedPatient && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded flex justify-between items-center">
                        <div>
                          <div className="font-medium">{selectedPatient.full_name}</div>
                          <div className="text-sm text-gray-600">{selectedPatient.phone}</div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setFormData({ ...formData, patient_id: '' })}
                        >
                          Change
                        </Button>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="new" className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        value={newPatientData.full_name}
                        onChange={(e) => setNewPatientData({...newPatientData, full_name: e.target.value})}
                        placeholder="Enter full name"
                      />
                    </div>
                    <div>
                      <Label>Phone *</Label>
                      <Input
                        value={newPatientData.phone}
                        onChange={(e) => setNewPatientData({...newPatientData, phone: e.target.value})}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={newPatientData.date_of_birth}
                        onChange={(e) => setNewPatientData({...newPatientData, date_of_birth: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Gender *</Label>
                      <Select value={newPatientData.gender} onValueChange={(val) => setNewPatientData({...newPatientData, gender: val})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select gender" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Male">Male</SelectItem>
                          <SelectItem value="Female">Female</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newPatientData.email}
                        onChange={(e) => setNewPatientData({...newPatientData, email: e.target.value})}
                        placeholder="Email address"
                      />
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Input
                        value={newPatientData.address}
                        onChange={(e) => setNewPatientData({...newPatientData, address: e.target.value})}
                        placeholder="Address"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Medical History</Label>
                    <Textarea
                      value={newPatientData.medical_history}
                      onChange={(e) => setNewPatientData({...newPatientData, medical_history: e.target.value})}
                      placeholder="Any medical history..."
                      rows={2}
                    />
                  </div>
                  <Button type="button" onClick={handleCreateNewPatient} disabled={loading}>
                    {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Create Patient & Continue
                  </Button>
                </TabsContent>
              </Tabs>

              {/* Doctor Selection with Search */}
              <div>
                <Label>Doctor * {selectedDoctor && `- ${selectedDoctor.specialist}`}</Label>
                <div className="relative">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search doctor by name or specialty..."
                      value={doctorSearch}
                      onChange={(e) => setDoctorSearch(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  {selectedDoctor && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded flex justify-between items-center">
                      <div>
                        <div className="font-medium">{selectedDoctor.full_name}</div>
                        <div className="text-sm text-gray-600">{selectedDoctor.specialist}</div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setFormData({ ...formData, doctor_id: '', doctor_name: '', doctor_nmc_number: '' })}
                      >
                        Change
                      </Button>
                    </div>
                  )}
                  {!selectedDoctor && filteredDoctors.length > 0 && (
                    <div className="mt-1 border rounded-md max-h-48 overflow-y-auto">
                      {filteredDoctors.slice(0, 10).map(doctor => (
                        <div
                          key={doctor.id}
                          className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleDoctorSelect(doctor.id)}
                        >
                          <div className="font-medium">{doctor.full_name || `${doctor.first_name} ${doctor.last_name}`}</div>
                          <div className="text-sm text-gray-600">{doctor.specialist}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Date and Time Slots */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Appointment Date *</Label>
                  <Input
                    type="date"
                    value={formData.appointment_date}
                    onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500 mt-1">Select today or future date only</p>

                </div>
                <div>
                  <Label>Time Slot *</Label>
                  <Select value={formData.appointment_time} onValueChange={(val) => setFormData({ ...formData, appointment_time: val })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select time slot" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {timeSlots.map(slot => (
                        <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Type and Notes */}
              <div>
                <Label>Appointment Type</Label>
                <Select value={formData.appointment_type} onValueChange={(val) => setFormData({ ...formData, appointment_type: val })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checkup">General Checkup</SelectItem>
                    <SelectItem value="followup">Follow-up</SelectItem>
                    <SelectItem value="consultation">Consultation</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  placeholder="Any notes..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={2}
                />
              </div>

              {/* Send Email */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sendEmail"
                  checked={formData.send_email}
                  onChange={(e) => setFormData({ ...formData, send_email: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="sendEmail" className="cursor-pointer">
                  Send confirmation email with QR code
                </Label>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading || !formData.patient_id || !formData.doctor_id}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Create Appointment
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Appointment Status</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {selectedAppointment && (
                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                  <p className="font-medium">{selectedAppointment.patient_name}</p>
                  <p className="text-sm text-gray-600">
                    {selectedAppointment.doctor_name} • {format(new Date(selectedAppointment.appointment_date), 'MMM dd, yyyy')} • {selectedAppointment.appointment_time}
                  </p>
                </div>
              )}
              
              <div>
                <Label>New Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select status..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="no_show">No Show</SelectItem>
                    <SelectItem value="rescheduled">Rescheduled</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Show date/time fields when rescheduled is selected */}
              {newStatus === 'rescheduled' && (
                <div className="space-y-3 border-l-4 border-purple-500 pl-4 py-2">
                  <p className="text-sm font-medium text-purple-900">Select New Date & Time:</p>
                  <div>
                    <Label>New Date</Label>
                    <Input
                      type="date"
                      value={rescheduleDate}
                      onChange={(e) => setRescheduleDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label>New Time</Label>
                    <Select value={rescheduleTime} onValueChange={setRescheduleTime}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select time slot..." />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map(slot => (
                          <SelectItem key={slot} value={slot}>{slot}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded text-sm text-blue-900">
                <strong>Status Guide:</strong>
                <ul className="mt-2 space-y-1 list-disc list-inside">
                  <li><strong>Scheduled:</strong> Appointment booked</li>
                  <li><strong>Confirmed:</strong> Patient confirmed attendance</li>
                  <li><strong>In Progress:</strong> Patient is currently with doctor</li>
                  <li><strong>Completed:</strong> Consultation finished</li>
                  <li><strong>Cancelled:</strong> Appointment cancelled</li>
                  <li><strong>No Show:</strong> Patient didn't show up</li>
                  <li><strong>Rescheduled:</strong> Moved to different time</li>
                </ul>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleStatusChange} disabled={loading || !newStatus}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
